import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import Objects from "./Objects";
import { setDisplayErrorSnackBar } from "../../../../../../features/userSlice";

const ImportSettingsTable = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams("");
  const reportType = searchParams.get("report") || "daily_user";
  const startDate = searchParams.get("start_date") || "";
  const endDate = searchParams.get("end_date") || "";

  //Brand Overview Fetcher Settings Data
  const brandOverviewFetcherSettingsData = useSelector(
    (state: any) =>
      state.brandConfigurationReport.brandOverviewFetcherSettingsData[
        reportType
      ],
  );

  //Brand Overview Object Section Data
  const objectSectionData = useSelector(
    (state: any) => state.brandConfigurationReport.objectSectionData,
  );

  //Brand Account Overview - to Get the Brand name
  const brandAccountOverviewInfo = useSelector(
    (state: any) => state.brandConfigurationReport.brandAccountOverviewInfo,
  );

  const [expandedIds, setExpandedIds] = useState<any>(null);

  const toggleExpand = (id: any) => {
    setExpandedIds((prev: any) => (prev === id ? null : id));
  };

  // Handle Raw Table ID click
  const handleRawTableClick = (e: React.MouseEvent, rawTableId: string) => {
    if (!startDate || !endDate) {
      e.preventDefault(); // block navigation if dates are not selected
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please choose the dates first.",
          type: "error",
        }),
      );
      return;
    }

    if (!rawTableId) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unable to navigate: missing raw table ID.",
          type: "error",
        }),
      );
      return;
    }

    // Build new URL and open in new tab
    const cleanBrandName = brandAccountOverviewInfo[0]?.brand_name
      .trim()
      .replace(/\s+/g, "_");

    const pathname = `/operations/brand_configuration_report/${cleanBrandName}_configuration_overview/raw_table_overview`;

    const newParams = new URLSearchParams(location.search);
    newParams.set("rawTableId", rawTableId);

    window.open(`${pathname}?${newParams.toString()}`, "_blank");
  };

  return (
    <>
      {/* Cards */}
      <Box display="flex" flexDirection="column" gap={1.5}>
        {brandOverviewFetcherSettingsData?.length > 0 &&
          brandOverviewFetcherSettingsData.map((table: any) => {
            const isExpanded = expandedIds === table?.raw_output_table_name;
            return (
              <Paper
                key={table.raw_output_table_name}
                sx={{
                  boxShadow: "none",
                  border: "1px solid #0000001F",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {/* Row 1: Raw Table ID | Collection Type | Report Name | Details */}
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr 1fr auto"
                  alignItems="center"
                  px={2}
                  py={1}
                  gap={2}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Raw Table ID
                    </Typography>
                    <Typography
                      variant="body2"
                      color="secondary"
                      fontWeight={500}
                    >
                      {/* <Link
                        style={{ color: "#9C27B0" }}
                        to={{
                          pathname: (() => {
                            const cleanBrandName =
                              brandAccountOverviewInfo[0]?.brand_name
                                .trim()
                                .replace(/\s+/g, "_"); //"BetVictor Sport UK New1" → "BetVictor_Sport_UK_New1"
                            return `/operations/brand_configuration_report/${cleanBrandName}_configuration_overview/raw_table_overview`;
                          })(),
                          search: (() => {
                            const newParams = new URLSearchParams(
                              location.search,
                            );
                            newParams.set(
                              "rawTableId",
                              table.raw_output_table_name,
                            );

                            return `?${newParams.toString()}`;
                          })(),
                        }}
                        target="_blank"
                      >
                        {" "}
                        {table.raw_output_table_name ?? ""}
                      </Link> */}

                      <span
                        style={{
                          color: "#9C27B0",
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontWeight: 500,
                        }}
                        onClick={(e) =>
                          handleRawTableClick(e, table.raw_output_table_name)
                        }
                      >
                        {table.raw_output_table_name ?? ""}
                      </span>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Collection Type
                    </Typography>
                    <Typography variant="body2">
                      {table.collection_type_name ?? ""}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Report Name
                    </Typography>
                    <Typography variant="body2">
                      {table.report_name ?? ""}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: "pointer", color: "text.secondary" }}
                    onClick={() => toggleExpand(table?.raw_output_table_name)}
                  >
                    <Typography variant="body2" sx={{ mr: 0.5 }}>
                      Details
                    </Typography>
                    <IconButton size="small">
                      {isExpanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Box>

                {/* Row 2 + Objects: Collapsible */}
                <Collapse in={isExpanded}>
                  <Divider />

                  {/* Row 2: Config Name | Format Type | Collection Method | Supports Filtering */}
                  <Box
                    display="grid"
                    gridTemplateColumns="1fr 1fr 1fr 1fr"
                    px={2}
                    py={1}
                    gap={2}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Config Name
                      </Typography>
                      <Typography variant="body2">
                        {table.config_name ?? ""}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Format Type
                      </Typography>
                      <Typography variant="body2">
                        {table.format_type ?? ""}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Collection Method
                      </Typography>
                      <Typography variant="body2">
                        {table.collection_method_name ?? ""}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Supports Filtering Based On
                      </Typography>
                      <Typography variant="body2">
                        {table.sub_filtering_method_name ?? ""}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Objects sub-table */}

                  <>
                    <Divider />
                    <Box px={2} pt={1} pb={0.5}>
                      <Typography variant="body2" fontWeight={600}>
                        Objects:
                      </Typography>
                    </Box>
                    <Objects
                      objectSectionData={objectSectionData}
                      currentTableId={table?.raw_output_table_name}
                    />
                  </>
                </Collapse>
              </Paper>
            );
          })}
      </Box>
    </>
  );
};

export default ImportSettingsTable;
